/*
 * Skeleton consumed by product-search-dialog only,
 * check the comments in packages/product-search-dialog/src/common/product-search-input/product-search-input-skeleton.tsx.
 */

import React from 'react';
import { useTheme } from '../theme';
import {
	MobileSearchIconWrapper,
	DesktopSearchIconWrapper,
	SearchInputContainer,
	SearchInputField,
} from './search-input.styled';
import { SearchAnchorWrapper } from '../search-anchor';
import { SearchIcon } from '../icons';
import { IconButton } from '@atlaskit/button/new';
import { getInputSkeletonFocus } from './query-store';
import { SSRsearchInput } from './ssr-interactive-search-input';

export interface Props {
	forwardRef?: React.Ref<HTMLInputElement>;
	features?: {
		interactiveSkeleton?: {
			enabled: boolean;
			advancedSearchUrl: string;
			placeholder: string;
			customScriptDataAttributes?: {
				[key: string]: string;
			};
		};
	};
	// Placeholder is defined separately from features to allow for a fallback placeholder for Jira (where the interactive skeleton
	// does not work because of Jira SSR)
	placeholder?: string;
	shouldFillContainer?: boolean;
	ariaLabel?: string;
	// Value to be set as the "data-ssr-placeholder" attribute on the container, used for VC calculation as
	// described in platform/packages/react-ufo/atlaskit/src/vc/vc-observer/observers/ssr-placeholders
	dataSsrPlaceholder?: string;
}

const MAX_INPUT_LENGTH = 500;

export const SearchInputSkeleton: React.FunctionComponent<Props> = (props) => {
	const theme = useTheme();
	const {
		forwardRef,
		features,
		shouldFillContainer,
		placeholder: nonFeaturesPlaceholder,
		ariaLabel,
		dataSsrPlaceholder,
	} = props;

	const { enabled, advancedSearchUrl, placeholder, customScriptDataAttributes } =
		features?.interactiveSkeleton || {};
	if (enabled || getInputSkeletonFocus()) {
		return (
			<SSRsearchInput
				shouldFillContainer={shouldFillContainer}
				advancedSearchUrl={advancedSearchUrl}
				placeholder={placeholder || nonFeaturesPlaceholder}
				customScriptDataAttributes={customScriptDataAttributes}
				theme={theme}
			/>
		);
	}

	return (
		<SearchAnchorWrapper
			shouldFillContainer={shouldFillContainer}
			data-ssr-placeholder={dataSsrPlaceholder}
		>
			<SearchInputContainer
				{...theme}
				shouldFillContainer={shouldFillContainer}
				isExpanded={false}
				role="search"
			>
				<MobileSearchIconWrapper isExpanded={false}>
					<IconButton
						label="Search"
						icon={() => <SearchIcon theme={theme} isExpanded={false} size="medium" />}
					/>
				</MobileSearchIconWrapper>
				<DesktopSearchIconWrapper {...theme} isExpanded={false}>
					<SearchIcon theme={theme} isExpanded={false} />
				</DesktopSearchIconWrapper>
				<SearchInputField
					data-test-id="search-dialog-search-skeleton"
					isExpanded={false}
					placeholder={nonFeaturesPlaceholder || placeholder}
					ref={forwardRef || undefined}
					disabled
					value=""
					maxLength={MAX_INPUT_LENGTH}
					onChange={() => {}}
					aria-label={ariaLabel || nonFeaturesPlaceholder || placeholder}
					{...theme}
				/>
			</SearchInputContainer>
		</SearchAnchorWrapper>
	);
};

export default React.forwardRef<HTMLInputElement, Props>((props, ref) => (
	<SearchInputSkeleton {...props} forwardRef={ref} />
));
